import { type CSSProperties, type ReactNode, useState } from 'react'
import type { Channel, Me, OrganisationAgent, UnreadLine } from '../api/types'
import { agentsIn, shortName, spokenName } from './agents'
import { machineShown, runnerDown } from './machines'
import { RouteLink } from '../app/RouteLink'
import { Badge, Face } from '../components/primitives'
import { radius } from '../components/primitives/shape'
import { activityLabel } from '../room/activity'
import { ColumnResizer, type ResizableColumn } from '../room/ColumnResizer'
import { displayName, roomName } from '../room/format'
import type { Rooms } from '../room/useRooms'
import { BlockedMark } from '../status/BlockedMark'
import { unreadTotal } from '../unread/counts'
import { OrganisationRail } from './OrganisationRail'

/**
 * How many rooms the list shows before "Show more".
 *
 * Nine, because ⌘1 to ⌘9 select a room by its position, and a shortcut should
 * land on a row you can see. Past the ninth a room still shows while you are in
 * it, while it has something unread or while one of your agents in it waits on
 * you: what folds away is the quiet rows, never one asking for you.
 */
const ROWS_BEFORE_MORE = 9

/** The column on a wide screen; on a phone, Home and the DMs tab. */
export type SidebarLayout = 'column' | 'home' | 'direct'

const ROW: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10, width: '100%', minWidth: 0, padding: '7px 8px',
  borderRadius: radius.sm, cursor: 'pointer', fontSize: 13, color: 'var(--text-dim)', textAlign: 'left',
}

const HEADING: CSSProperties = {
  display: 'flex', justifyContent: 'space-between', gap: 8, padding: '0 8px 4px',
  fontSize: 11.5, color: 'var(--text-faint)',
}

/** A section keeps its height; the area around it scrolls instead. */
const SECTION: CSSProperties = { display: 'flex', flexDirection: 'column', flex: 'none' }

const ELLIPSIS: CSSProperties = { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }

/** Where the person's folds are remembered: the Rooms heading, and each room's agents. */
const ROOMS_FOLD = 'stagepost.fold.rooms'
const roomFold = (channelId: string) => `stagepost.fold.room.${channelId}`

function storedFold(key: string): boolean | null {
  try {
    const value = localStorage.getItem(key)
    return value === null ? null : value === 'folded'
  } catch {
    // Storage turned off: every fold starts at its default.
    return null
  }
}

function storeFold(key: string, folded: boolean): void {
  try {
    localStorage.setItem(key, folded ? 'folded' : 'open')
  } catch {
    // Back to the default on the next load, which is survivable.
  }
}

const ARROW_WIDTH = 16

/** A chevron for a `.sp-fold` button, which turns it down while the button is expanded. */
const ARROW = (
  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 12 12" style={{ flex: 'none' }}>
    <path d="M4.5 2.5 8 6l-3.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const LIST: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 2, margin: 0, padding: 0, listStyle: 'none' }

/** Newest first, by the id of the newest unread message, which sorts by time. */
function byNewest<Row>(lineOf: (row: Row) => UnreadLine | null | undefined) {
  return (first: Row, second: Row) => {
    const [one, other] = [lineOf(first)?.id ?? '', lineOf(second)?.id ?? '']
    return one === other ? 0 : one < other ? 1 : -1
  }
}

function Item({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return <button type="button" onClick={onClick} style={ROW}>{children}</button>
}

function ShowMore({ folded, open, onToggle }: { folded: number; open: boolean; onToggle: () => void }) {
  if (folded === 0) return null
  return (
    <button type="button" onClick={onToggle} aria-expanded={open} style={{ ...ROW, color: 'var(--text-faint)', fontSize: 12.5 }}>
      {open ? 'Show fewer' : `Show ${folded} more`}
    </button>
  )
}

/**
 * Where everything in an organisation is reached from, in Slack's order: the
 * organisation, Activity, Search, Rooms with your agents under each, then the
 * places you visit and yourself. A phone shows it as Home, which lists the rooms
 * with a line on your agents in each, and the DMs tab lists your agents under
 * their rooms.
 */
export function Sidebar({
  layout, me, rooms, column, directOpen, unreadsOpen = false, onOpenRoom, onOpenDirect, onUnreads, onActivity, onSearch,
  onOpen, onLeave,
}: {
  layout: SidebarLayout
  me: Me
  rooms: Rooms
  /** The column's width, on a wide screen. */
  column?: ResizableColumn
  /** The agent whose direct message is open, which takes the mark from the room. */
  directOpen?: string | null
  /** The Unreads list is in the room's place. */
  unreadsOpen?: boolean
  onOpenRoom: (channel: Channel) => void
  onOpenDirect: (agent: string) => void
  onUnreads: () => void
  onActivity: () => void
  onSearch: () => void
  onOpen: (place: 'lessons' | 'setup' | 'settings') => void
  /** After switching to or making another organisation. */
  onLeave: () => void
}) {
  const { organisations, organisation, channels, activity } = rooms
  const agents = rooms.agents ?? []
  const phone = layout !== 'column'
  const [everyRoomListed, setEveryRoomListed] = useState(false)
  const [roomsFolded, setRoomsFolded] = useState(() => storedFold(ROOMS_FOLD) ?? false)
  const [roomFolds, setRoomFolds] = useState<Record<string, boolean>>({})
  const machine = rooms.machines && machineShown(rooms.machines)
  const dot = machine && (
    <span aria-hidden="true" style={{
      width: 6, height: 6, borderRadius: '50%', flex: 'none',
      background: machine.runner_online ? 'var(--online)' : 'var(--text-faint)',
    }} />
  )

  const waitingIn = (channel: Channel) => agentsIn(agents, channel.id).reduce((sum, agent) => sum + agent.waiting, 0)
  const asking = (channel: Channel) => channel.id === rooms.channel?.id || waitingIn(channel) > 0
  const quietRooms = channels.filter((channel, index) =>
    index >= ROWS_BEFORE_MORE && !asking(channel) && channel.unread === 0)
  const headingFolded = !phone && roomsFolded
  const listedRooms = headingFolded ? channels.filter(asking)
    : everyRoomListed ? channels : channels.filter((channel) => !quietRooms.includes(channel))
  const roomFolded = (channel: Channel) => roomFolds[channel.id] ?? storedFold(roomFold(channel.id)) ?? !asking(channel)
  const running = agents.filter((agent) => agent.online).length

  const header = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: phone ? '0 8px 4px' : '0 8px' }}>
      <div style={{ ...ELLIPSIS, flex: 1, font: `700 ${phone ? 20 : 15}px var(--font-interface)` }}>
        {organisation?.name ?? 'stagepost'}
      </div>
      <OrganisationRail
        organisations={organisations.filter((other) => other.slug !== organisation?.slug)}
        current={organisation?.slug}
        // A thumb's 44px around a 22px tile reaches into the next one.
        size={phone ? 34 : 22}
        onSelect={(slug) => { rooms.selectOrganisation(slug); onLeave() }}
        onCreate={async (name) => { await rooms.createOrganisation(name); onLeave() }}
      />
      {phone && <Face name={me.display_name} size={32} avatarKey={me.avatar_key} />}
    </div>
  )

  const search = phone ? (
    <button
      type="button"
      onClick={onSearch}
      style={{ ...ROW, padding: '10px 12px', background: 'var(--panel-strong)', borderRadius: radius.md, color: 'var(--text-faint)' }}
    >
      Search
    </button>
  ) : <Item onClick={onSearch}>Search</Item>

  const agentRow = (agent: OrganisationAgent) => {
    const where = [agent.surface, agent.title ?? agent.summary].filter(Boolean).join(' · ')
    const here = agent.name === directOpen && !phone
    return (
      <button
        key={agent.name}
        type="button"
        onClick={() => onOpenDirect(agent.name)}
        aria-current={here}
        style={{
          ...ROW, opacity: agent.online ? 1 : 0.5,
          padding: phone ? '9px 8px' : `5px 8px 5px ${8 + ARROW_WIDTH + 4}px`,
          background: here ? 'var(--panel-strong)' : 'transparent',
        }}
      >
        {/* The label says the handle; the face would say it again. */}
        <span aria-hidden="true" style={{ display: 'flex', flex: 'none' }}>
          <Face name={agent.name} size={phone ? 28 : 20} avatarKey={agent.avatar_key} />
        </span>
        <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span
            title={agent.name}
            aria-label={spokenName(agent)}
            style={{
              ...ELLIPSIS, color: 'var(--text)',
              font: `500 ${phone ? 14 : 12.5}px var(${agent.nickname ? '--font-interface' : '--font-machine'})`,
            }}
          >
            {shortName(agent)}
          </span>
          {phone && where && <span style={{ ...ELLIPSIS, fontSize: 12, color: 'var(--text-meta)' }}>{where}</span>}
          {/* On a desk only the session's title earns a second line: it is the name the person gave the session. */}
          {!phone && agent.title && (
            <span style={{ ...ELLIPSIS, fontSize: 11.5, color: 'var(--text-meta)' }}>{agent.title}</span>
          )}
        </span>
        {agent.waiting > 0 ? (
          <Badge count={agent.waiting} label={`${agent.waiting} waiting on you`} />
        ) : agent.online && agent.activity === 'blocked' ? (
          <span style={{ ...ELLIPSIS, flex: '0 1 auto', fontSize: 11, color: 'var(--text-soft)' }}>
            {activityLabel(true, agent.activity)}
          </span>
        ) : agent.plan ? (
          <span style={{ font: '500 11px var(--font-machine)', color: 'var(--online)' }}>
            {agent.plan.done}/{agent.plan.total}
          </span>
        ) : !agent.online ? (
          <span style={{ font: '400 11px var(--font-machine)', color: 'var(--text-faint)' }}>done</span>
        ) : agent.activity && (
          <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>{activityLabel(true, agent.activity)}</span>
        )}
      </button>
    )
  }

  const noAgents = rooms.agents?.length === 0 && (
    <p style={{ margin: 0, padding: '4px 8px', fontSize: 12, lineHeight: 1.5, color: 'var(--text-faint)' }}>
      No agents running. Type /agent in a room to start one.
    </p>
  )

  const runningCount = running > 0 && <span>{running} {running === 1 ? 'agent' : 'agents'} running</span>

  const here = (channel: Channel) => channel.id === rooms.channel?.id && !phone && !directOpen && !unreadsOpen

  /** `beside` sits before the unread count; `under` is a line beneath the name. */
  const roomButton = (channel: Channel, beside: ReactNode = null, under: ReactNode = null) => {
    const name = (
      <span
        title={channel.repository_key}
        style={{ ...ELLIPSIS, flex: 1, fontSize: phone ? 15 : 13.5, color: here(channel) || channel.unread ? 'var(--text)' : 'var(--text-dim)' }}
      >
        {roomName(channel)}
      </span>
    )
    return (
      <button
        type="button"
        onClick={() => onOpenRoom(channel)}
        aria-current={here(channel)}
        style={{ ...ROW, flex: phone ? 'none' : 1, paddingLeft: phone ? 8 : 0 }}
      >
        <span aria-hidden="true" style={{ color: 'var(--text-faint)' }}>#</span>
        {under ? (
          <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>{name}{under}</span>
        ) : name}
        <BlockedMark since={channel.blocked_since} />
        {beside}
        <Badge count={channel.unread} tone="dim" />
      </button>
    )
  }

  const phoneRoom = (channel: Channel) => {
    const crew = agentsIn(agents, channel.id)
    const asked = crew.filter((agent) => agent.waiting > 0).length
    const waiting = waitingIn(channel)
    return roomButton(
      channel,
      <Badge count={waiting} label={`${waiting} waiting on you`} />,
      crew.length > 0 && (
        <span style={{ ...ELLIPSIS, fontSize: 12, color: 'var(--text-meta)' }}>
          {crew.length} {crew.length === 1 ? 'agent' : 'agents'}
          {asked > 0 && <> · <span style={{ color: 'var(--accent)' }}>{asked} waiting on you</span></>}
        </span>
      ),
    )
  }

  const roomTree = (channel: Channel) => {
    const name = roomName(channel)
    const crew = agentsIn(agents, channel.id)
    const folded = roomFolded(channel)
    const waiting = waitingIn(channel)
    const shown = folded ? crew.filter((agent) => agent.waiting > 0) : crew
    const fold = () => {
      storeFold(roomFold(channel.id), !folded)
      setRoomFolds((chosen) => ({ ...chosen, [channel.id]: !folded }))
    }
    return (
      <>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 4, paddingLeft: 4, borderRadius: radius.sm,
          background: here(channel) ? 'var(--panel-strong)' : 'transparent',
        }}>
          {crew.length > 0 ? (
            <button
              type="button"
              onClick={fold}
              aria-expanded={!folded}
              aria-label={`Agents in ${name}`}
              className="sp-tap sp-fold"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none',
                width: ARROW_WIDTH, height: ARROW_WIDTH, padding: 0, cursor: 'pointer',
              }}
            >
              {ARROW}
            </button>
          ) : <span style={{ width: ARROW_WIDTH, flex: 'none' }} />}
          {roomButton(channel, folded && crew.length > 0 && (
            <>
              <span style={{ font: '400 11px var(--font-machine)', color: 'var(--text-faint)', whiteSpace: 'nowrap' }}>
                {crew.length} {crew.length === 1 ? 'agent' : 'agents'}
              </span>
              <Badge count={waiting} label={`${waiting} waiting on you`} />
            </>
          ))}
        </div>
        {shown.length > 0 && (
          <ul aria-label={`Agents in ${name}`} style={{ ...LIST, marginTop: 2 }}>
            {shown.map((agent) => <li key={agent.name}>{agentRow(agent)}</li>)}
          </ul>
        )}
      </>
    )
  }

  const quietFolded = channels.length - listedRooms.length

  const roomList = (
    <section aria-label="Rooms" style={SECTION}>
      {phone ? <div style={HEADING}><span>Rooms</span>{runningCount}</div> : (
        <div style={HEADING}>
          <button
            type="button"
            onClick={() => { storeFold(ROOMS_FOLD, !roomsFolded); setRoomsFolded(!roomsFolded) }}
            aria-expanded={!roomsFolded}
            className="sp-tap sp-fold"
            style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: -2, padding: 0, cursor: 'pointer' }}
          >
            {ARROW}
            Rooms
          </button>
          {runningCount}
        </div>
      )}
      {noAgents}
      <ul style={LIST}>
        {listedRooms.map((channel) => <li key={channel.id}>{phone ? phoneRoom(channel) : roomTree(channel)}</li>)}
      </ul>
      {headingFolded ? quietFolded > 0 && (
        <p style={{ margin: 0, padding: '7px 8px', font: '400 11px var(--font-machine)', color: 'var(--text-faint)' }}>
          {quietFolded} quiet {quietFolded === 1 ? 'room' : 'rooms'} folded
        </p>
      ) : <ShowMore folded={quietRooms.length} open={everyRoomListed} onToggle={() => setEveryRoomListed((listed) => !listed)} />}
    </section>
  )

  const unreadRow = (key: string, mark: ReactNode, name: ReactNode, line: ReactNode, badge: ReactNode, onClick: () => void) => (
    <li key={key}>
      <button type="button" onClick={onClick} style={{ ...ROW, padding: '9px 8px' }}>
        {mark}
        <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>{name}{line}</span>
        {badge}
      </button>
    </li>
  )

  // A private message is the agent's own, which the row already names.
  const newestLine = (line: UnreadLine | null | undefined, withSender: boolean) => line && (
    <span style={{ ...ELLIPSIS, fontSize: 12, color: 'var(--text-meta)' }}>
      {withSender && (
        <><span style={{ fontWeight: 600, color: 'var(--text-soft)' }}>{displayName(line.sender, line.sender_nickname)}:</span>{' '}</>
      )}
      {line.headline}
    </span>
  )

  // From the organisation stream alone: a room is listed once the stream has said what is newest in it.
  const unreadAgents = agents.filter((agent) => agent.direct_unread > 0).sort(byNewest((agent) => agent.newest_direct_unread))
  const unreadRooms = channels.filter((channel) => channel.unread > 0 && channel.newest_unread)
    .sort(byNewest((channel) => channel.newest_unread))
  const unreadSection = (unreadAgents.length > 0 || unreadRooms.length > 0) && (
    <section aria-label="Unread" style={SECTION}>
      <div style={HEADING}>
        <span>Unread</span>
        <button type="button" onClick={onUnreads} style={{ padding: 0, cursor: 'pointer', fontWeight: 700, color: 'var(--accent)' }}>
          See all as a list
        </button>
      </div>
      <ul style={LIST}>
        {unreadAgents.map((agent) => unreadRow(
          agent.name,
          <span aria-hidden="true" style={{ display: 'flex', flex: 'none' }}>
            <Face name={agent.name} size={28} avatarKey={agent.avatar_key} />
          </span>,
          <span
            title={agent.name}
            aria-label={spokenName(agent)}
            style={{ ...ELLIPSIS, color: 'var(--text)', font: `500 14px var(${agent.nickname ? '--font-interface' : '--font-machine'})` }}
          >
            {shortName(agent)}
          </span>,
          newestLine(agent.newest_direct_unread, false),
          <Badge count={agent.direct_unread} />,
          () => onOpenDirect(agent.name),
        ))}
        {unreadRooms.map((channel) => unreadRow(
          channel.id,
          <span aria-hidden="true" style={{ width: 28, flex: 'none', textAlign: 'center', color: 'var(--text-faint)' }}>#</span>,
          <span title={channel.repository_key} style={{ ...ELLIPSIS, fontSize: 15, color: 'var(--text)' }}>{roomName(channel)}</span>,
          newestLine(channel.newest_unread, true),
          <Badge count={channel.unread} tone="dim" />,
          () => onOpenRoom(channel),
        ))}
      </ul>
    </section>
  )

  const total = unreadTotal(channels, agents)

  const places = (
    <>
      <Item onClick={() => onOpen('lessons')}>Lessons</Item>
      <Item onClick={() => onOpen('setup')}>Setup</Item>
      <RouteLink to="/guide" style={ROW}>Guide</RouteLink>
    </>
  )

  if (layout === 'direct') {
    return (
      <nav aria-label="DMs" className="sp-phone-screen">
        <div style={{ padding: '0 8px', font: '700 20px var(--font-interface)' }}>DMs</div>
        {noAgents}
        {channels.map((channel) => {
          const crew = agentsIn(agents, channel.id)
          return crew.length > 0 && (
            <section key={channel.id} aria-label={roomName(channel)} style={SECTION}>
              <div style={HEADING}># {roomName(channel)}</div>
              {crew.map(agentRow)}
            </section>
          )
        })}
      </nav>
    )
  }

  if (layout === 'home') {
    return (
      <nav aria-label="Home" className="sp-phone-screen">
        {header}
        {rooms.machines && (
          <div style={{ ...ROW, cursor: 'default', padding: '10px 12px', background: 'var(--panel-strong)', borderRadius: radius.md }}>
            {dot}
            <div style={{ flex: 1, minWidth: 0, color: 'var(--text-faint)' }}>
              {machine ? (
                <>
                  <div style={{ ...ELLIPSIS, fontWeight: 600, color: 'var(--text)' }}>{machine.name}</div>
                  <div style={ELLIPSIS}>
                    {machine.runner_online
                      ? `Ready to start agents${machine.permission_mode ? ` · ${machine.permission_mode}` : ''}`
                      : `Runner ${runnerDown(machine)} · agents cannot start`}
                  </div>
                </>
              ) : <div style={ELLIPSIS}>No machine set up · agents cannot start</div>}
            </div>
            {!machine?.runner_online && (
              // A tap target 34px tall that takes no more room in the card than its text.
              <button
                type="button"
                onClick={() => onOpen('setup')}
                style={{ padding: 8, margin: -8, fontSize: 13, lineHeight: '18px', fontWeight: 600, color: 'var(--accent)', cursor: 'pointer' }}
              >
                {machine ? 'Fix' : 'Set up'}
              </button>
            )}
          </div>
        )}
        {search}
        {unreadSection}
        {roomList}
        <div>{places}</div>
      </nav>
    )
  }

  return (
    <nav
      aria-label="Sidebar"
      style={{
        position: 'relative', width: column?.width, flex: 'none',
        display: 'flex', flexDirection: 'column', gap: 18, padding: '16px 8px 14px',
      }}
    >
      {column && <ColumnResizer column={column} label="Resize the sidebar" />}
      {header}
      <div style={{
        flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehaviorY: 'contain',
        display: 'flex', flexDirection: 'column', gap: 18,
      }}>
        <div style={SECTION}>
          <button
            type="button"
            onClick={onUnreads}
            aria-current={unreadsOpen}
            style={{ ...ROW, background: unreadsOpen ? 'var(--panel-strong)' : 'transparent', color: unreadsOpen ? 'var(--text)' : ROW.color }}
          >
            <span style={{ flex: 1 }}>Unreads</span>
            <Badge count={total} tone="dim" />
          </button>
          <Item onClick={onActivity}>
            <span style={{ flex: 1 }}>Activity</span>
            <Badge count={activity.length} label={`${activity.length} in activity`} />
          </Item>
          {search}
        </div>
        {roomList}
      </div>
      <div>
        {places}
        <Item onClick={() => onOpen('settings')}>Settings</Item>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 8px 0', borderTop: '1px solid var(--border)' }}>
        <Face name={me.display_name} size={28} avatarKey={me.avatar_key} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700 }}>{me.display_name}</div>
          {machine && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-faint)' }}>
              {dot}
              <span style={ELLIPSIS}>
                {machine.name} · {machine.runner_online ? 'ready to start agents' : `runner ${runnerDown(machine)}`}
              </span>
            </div>
          )}
        </div>
      </div>
    </nav>
  )
}
